import { Injectable } from '@angular/core';
import { BehaviorSubject, of } from 'rxjs';
import { chartColors } from '../../../../Common/Charts/enums/chart-colors.enum';
import { DataPointModel } from '../../../../Common/Charts/models/data-point.model';
import { SeriesModel } from '../../../../Common/Charts/models/series.model';
import { ThemeAnalyticsChartTypeEnum } from '../../enums/theme-analytics-chart-type.enum';
import { ReportSeriesModel } from '@Common/Reports/models/report-series.model';


@Injectable()
export class ThemeAnalyticsChartSeriesService {

    shareOfVoiceBySocialTopicSubChartSeries: ReportSeriesModel[];
    sentimentSummaryChartSeries: ReportSeriesModel[];
    sentimentBySocialMediaComparisonChartSeries: SeriesModel[];

    chartTypeToSeriesMap: Map<ThemeAnalyticsChartTypeEnum, SeriesModel[]>;

    constructor() {
        this.setShareOfVoiceBySocialTopicSubChartSeries();
        this.setSentimentSummaryChartSeries();
        this.setSentimentBySocialMediaComparisonChartSeries();

        this.chartTypeToSeriesMap = new Map<ThemeAnalyticsChartTypeEnum, SeriesModel[]>(
            [
                [ThemeAnalyticsChartTypeEnum.SENTIMENT_SUMMARY, this.sentimentSummaryChartSeries],
                [ThemeAnalyticsChartTypeEnum.SHARE_OF_VOICE_BY_SOCIAL_TOPIC_SUB, this.shareOfVoiceBySocialTopicSubChartSeries],
                [ThemeAnalyticsChartTypeEnum.SENTIMENT_BY_SOCIAL_MEDIA_COMPARISON, this.sentimentBySocialMediaComparisonChartSeries]
            ]
        );
    }

    private setSentimentSummaryChartSeries(): void {
        const previousPeriod = new ReportSeriesModel();
        previousPeriod.label = 'Previous Period';
        previousPeriod.dataProvider = new BehaviorSubject(
            [
                {
                    id: 'POSITIVE',
                    label: 'Positive',
                    xValue: Math.round(Math.random() * 200)
                },
                {
                    id: 'NEUTRAL',
                    label: 'Neutral',
                    xValue: Math.round(Math.random() * 200)
                },
                {
                    id: 'NEGATIVE',
                    label: 'Negative',
                    xValue: Math.round(Math.random() * 200)
                },
                {
                    id: 'NOT_AVAILABLE',
                    label: 'Not Available',
                    xValue: Math.round(Math.random() * 200)
                }
            ]
        );
        const currentPeriod = new ReportSeriesModel();
        currentPeriod.label = 'Current Period';
        currentPeriod.dataProvider = new BehaviorSubject(
            [
                {
                    id: 'POSITIVE',
                    label: 'Positive',
                    xValue: Math.round(Math.random() * 200)
                },
                {
                    id: 'NEUTRAL',
                    label: 'Neutral',
                    xValue: Math.round(Math.random() * 200)
                },
                {
                    id: 'NEGATIVE',
                    label: 'Negative',
                    xValue: Math.round(Math.random() * 200)
                },
                {
                    id: 'NOT_AVAILABLE',
                    label: 'Not Available',
                    xValue: Math.round(Math.random() * 200)
                }
            ]
        );
        this.sentimentSummaryChartSeries = [previousPeriod, currentPeriod];
    }

    private setShareOfVoiceBySocialTopicSubChartSeries(): void {
        const previousPeriod = new ReportSeriesModel();
        previousPeriod.label = 'Previous Period';
        previousPeriod.dataProvider = new BehaviorSubject([
            {
                id: 'TWITTER',
                label: 'X',
                xValue: Math.round(Math.random() * 200)
            },
            {
                id: 'FACEBOOK',
                label: 'Facebook',
                xValue: Math.round(Math.random() * 200)
            },
            {
                id: 'INSTAGRAM',
                label: 'Instagram',
                xValue: Math.round(Math.random() * 200)
            },
            {
                id: 'YOUTUBE',
                label: 'YouTube',
                xValue: Math.round(Math.random() * 200)
            },
            {
                id: 'REDDIT',
                label: 'Reddit',
                xValue: Math.round(Math.random() * 200)
            }
        ]);

        const currentPeriod = new ReportSeriesModel();
        currentPeriod.label = 'Previous Period';
        currentPeriod.dataProvider = new BehaviorSubject([
            {
                id: 'TWITTER',
                label: 'X',
                xValue: Math.round(Math.random() * 200)
            },
            {
                id: 'FACEBOOK',
                label: 'Facebook',
                xValue: Math.round(Math.random() * 200)
            },
            {
                id: 'INSTAGRAM',
                label: 'Instagram',
                xValue: Math.round(Math.random() * 200)
            },
            {
                id: 'YOUTUBE',
                label: 'YouTube',
                xValue: Math.round(Math.random() * 200)
            },
            {
                id: 'REDDIT',
                label: 'Reddit',
                xValue: Math.round(Math.random() * 200)
            }
        ]);

        this.shareOfVoiceBySocialTopicSubChartSeries = [previousPeriod, currentPeriod];
    }

    private setSentimentBySocialMediaComparisonChartSeries(): void {
        this.sentimentBySocialMediaComparisonChartSeries = [
            {
                label: 'not in use',
                dataProvider: of([
                    {
                        id: 'POSITIVE',
                        source: 'Facebook',
                        yValue: Math.round(Math.random() * 100),
                        color: chartColors.agprPositive as string,
                        labelColor: chartColors.agprText,
                    } as DataPointModel,
                    {
                        id: 'NEUTRAL',
                        source: 'Facebook',
                        yValue: Math.round(Math.random() * 100),
                        color: chartColors.agprNeutral as string,
                        labelColor: chartColors.agprText,
                    } as DataPointModel,
                    {
                        id: 'NOT_AVAILABLE',
                        source: 'Facebook',
                        yValue: Math.round(Math.random() * 100),
                        color: chartColors.agprNotAvailable as string,
                        labelColor: chartColors.agprText,
                    } as DataPointModel,
                    {
                        id: 'NEGATIVE',
                        source: 'Facebook',
                        yValue: Math.round(Math.random() * 100),
                        color: chartColors.agprNegative as string,
                        labelColor: chartColors.agprText,
                    } as DataPointModel,
                    {
                        id: 'POSITIVE',
                        source: 'Twitter',
                        yValue: Math.round(Math.random() * 100),
                        color: chartColors.agprPositive as string,
                        labelColor: chartColors.agprText,
                    } as DataPointModel,
                    {
                        id: 'NEUTRAL',
                        source: 'Twitter',
                        yValue: Math.round(Math.random() * 100),
                        color: chartColors.agprNeutral as string,
                        labelColor: chartColors.agprText,
                    } as DataPointModel,
                    {
                        id: 'NOT_AVAILABLE',
                        source: 'Twitter',
                        yValue: Math.round(Math.random() * 100),
                        color: chartColors.agprNotAvailable as string,
                        labelColor: chartColors.agprText,
                    } as DataPointModel,
                    {
                        id: 'NEGATIVE',
                        source: 'Twitter',
                        yValue: Math.round(Math.random() * 100),
                        color: chartColors.agprNegative as string,
                        labelColor: chartColors.agprText,
                    } as DataPointModel,
                    {
                        id: 'POSITIVE',
                        source: 'YouTube',
                        yValue: Math.round(Math.random() * 100),
                        color: chartColors.agprPositive as string,
                        labelColor: chartColors.agprText,
                    } as DataPointModel,
                    {
                        id: 'NEUTRAL',
                        source: 'YouTube',
                        yValue: Math.round(Math.random() * 100),
                        color: chartColors.agprNeutral as string,
                        labelColor: chartColors.agprText,
                    } as DataPointModel,
                    {
                        id: 'NOT_AVAILABLE',
                        source: 'YouTube',
                        yValue: Math.round(Math.random() * 100),
                        color: chartColors.agprNotAvailable as string,
                        labelColor: chartColors.agprText,
                    } as DataPointModel,
                    {
                        id: 'NEGATIVE',
                        source: 'YouTube',
                        yValue: Math.round(Math.random() * 100),
                        color: chartColors.agprNegative as string,
                        labelColor: chartColors.agprText,
                    } as DataPointModel,
                ] as DataPointModel[])
            }
        ] as SeriesModel[];
    }

    getChartSeries(chartType: ThemeAnalyticsChartTypeEnum): SeriesModel | SeriesModel[] {
        return this.chartTypeToSeriesMap.get(chartType);
    }
}
